/**
 * แก้ไขข้อมูลสินค้าที่ยังเป็นร่าง — `PATCH /v1/products/{product_id}`
 *
 * หน้าจอนี้มีอยู่เพื่อกรณีเดียว: รายการที่ยังเป็น `DRAFT` และยังไม่ถูกส่งอนุมัติ
 * ถูกกรอกผิด และต้องแก้ให้ถูกก่อนส่ง สิ่งที่จงใจ "ไม่มี" ที่นี่ และเหตุผล:
 *
 * - **ไม่มีช่องแก้รหัสสินค้า** — `PATCH` ไม่รับ `product_code` เพราะรหัสธุรกิจของ
 *   record ที่สร้างแล้วเปลี่ยนไม่ได้ ช่องนี้จึงยัง *แสดง* อยู่แบบอ่านอย่างเดียว
 *   พร้อมเหตุผล แทนที่จะหายไปเฉย ๆ แล้วปล่อยให้ผู้ใช้สงสัยว่ารหัสหายไปไหน
 * - **ไม่มีช่องสถานะและช่องผู้กระทำ** — เหตุผลเดียวกับตอนสร้าง สถานะขยับผ่าน
 *   transition เท่านั้น และผู้กระทำมาจาก auth context (D-010)
 * - **ไม่มี `expected_status` และไม่มี `If-Match`** — `docs/API_CONTRACT.md` ตัดสินว่า
 *   `PATCH` บน `DRAFT` ยอมรับ last-write-wins ได้ เพราะยังไม่ผ่านการอนุมัติและ
 *   แก้ซ้ำได้ ส่วน guard แบบนั้นสงวนไว้ให้ transition ซึ่งเป็นจุดที่ stale view
 *   อันตรายจริง การเติมเข้ามาเองจะได้ด่านปลอมที่เซิร์ฟเวอร์ไม่ได้อ่าน
 * - **ไม่มีฟอร์มคำขอเปลี่ยนแปลงในหน้านี้** — มันเป็นคนละ resource กับ `PATCH`
 *   และมีหน้าจอของตัวเอง (`ProductChangeRequestView`) หน้านี้จึงมีแค่ลิงก์พาไป
 *   ผ่าน `ProductChangeRequestNote` (D-011/D-042)
 * - **ไม่มีการเพิ่มหรือลบมุมมองโรงงาน** — หน้าจอนี้แก้เฉพาะโรงงานที่ record มีอยู่แล้ว
 *   และผู้ใช้มีสิทธิ์เห็น (D-004) การเพิ่มโรงงานใหม่เป็นการตัดสินใจคนละเรื่อง และ
 *   ยังไม่มี endpoint ให้ดูรายชื่อโรงงานใน scope มาเลือก การให้พิมพ์รหัสเดาเอา
 *   แล้วโดน `403` ไม่ใช่หน้าจอที่ช่วยใคร
 *
 * **สองอย่างที่หน้าจอนี้แยกจากกันตลอดเวลา** คือ "ค่าที่เซิร์ฟเวอร์ยืนยันแล้ว" กับ
 * "ค่าที่กำลังพิมพ์อยู่" ความล้มเหลวไม่ขยับก้อนแรกแม้แต่ช่องเดียว และก้อนแรกจะ
 * เปลี่ยนได้ทางเดียวคือเซิร์ฟเวอร์ตอบ resource ใหม่กลับมา — ฟอร์มที่ค้างค่าที่พิมพ์
 * ไว้บนจอโดยไม่บอกว่ายังไม่ถูกบันทึก คืออุบัติเหตุชนิดเดียวกับ stale view
 *
 * record ที่โหลดมาไม่ถูกเขียนลง storage ใด ๆ (D-049 อนุญาตเฉพาะ session token)
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductChangeRequestNote } from './ProductChangeRequestNote';
import { ProductFailureNotice } from './ProductFailureNotice';
import { ProductSteelFields } from './ProductSteelFields';
import {
  detailSearch,
  productStatusLabel,
  productStatusTone,
  orDash,
  steelProblems,
  steelSummary,
  steelTextsFromProduct,
  type SteelTexts,
} from './productView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { fetchProduct, updateProduct, type ProductFailure } from '../../lib/products/client';
import {
  PRODUCT_EDITABLE_STATUS,
  PRODUCT_STEEL_FIELDS,
  type Product,
  type ProductPlantData,
  type ProductSteelField,
  type UpdateProductBody,
  type UpdateProductPlantInput,
} from '../../lib/products/types';

export interface ProductEditViewProps {
  readonly productId: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

/** ค่าที่เซิร์ฟเวอร์ยืนยันแล้ว — ไม่ใช่ค่าที่กำลังพิมพ์ */
type RecordState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly product: Product }
  | ProductFailure;

type SaveState =
  | { readonly status: 'idle' }
  | { readonly status: 'saving' }
  | { readonly status: 'saved' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | ProductFailure;

interface PlantFormState {
  /** คีย์ที่ใช้จับคู่กับมุมมองโรงงานเดิม — แก้ไม่ได้จากหน้าจอนี้ */
  readonly plant_code: string;
  readonly lot_size_procedure: string;
  readonly planned_delivery_time: string;
  readonly safety_stock: string;
  readonly unit_cost: string;
  readonly valuation_class: string;
}

interface EditFormState {
  readonly name: string;
  /** ช่องว่างที่นี่ไม่ได้แปลว่า "ล้างค่า" เสมอไป — ดู `modelPatchValue()` */
  readonly model: string;
  readonly product_type: string;
  readonly base_uom: string;
  /** น้ำหนักต่อชิ้นและข้อมูลเหล็ก (D-165) — ช่องว่างที่เคยมีค่า = ล้างค่า */
  readonly steel: SteelTexts;
  readonly plants: readonly PlantFormState[];
}

/**
 * ช่องระดับ product ที่ contract ยอมให้แก้ — `product_code` ไม่อยู่ในนี้โดยตั้งใจ
 *
 * `model` เข้าชุดนี้เพื่อใช้ `updateField()` ตัวเดียวกัน แต่ **ไม่อยู่ในรายการ
 * required ของ `buildBody()`** เพราะเป็นฟิลด์ที่เว้นว่างได้ (D-107 ข้อ 1)
 */
type GeneralField = 'name' | 'model' | 'product_type' | 'base_uom';

/** เลขจำนวนเต็มไม่ติดลบเท่านั้น — `planned_delivery_time` เป็น INT ไม่ใช่ NUMERIC */
const INTEGER_PATTERN = /^\d+$/;

/**
 * ค่าที่เป็น `null` จากเซิร์ฟเวอร์กลายเป็นช่องว่างในฟอร์ม และช่องว่างกลายเป็น `null`
 * กลับไป — ความหมาย "ยังไม่มีค่า" จึงเดินทางไปกลับได้โดยไม่เพี้ยนเป็นสตริงว่าง
 */
function fieldText(value: string | number | null): string {
  return value === null ? '' : String(value);
}

function plantForm(plant: ProductPlantData): PlantFormState {
  return {
    plant_code: plant.plant_code,
    lot_size_procedure: fieldText(plant.lot_size_procedure),
    planned_delivery_time: fieldText(plant.planned_delivery_time),
    // ⚠️ ตัวเลข NUMERIC เดินทางเป็น string ตลอดเส้นทาง ไม่แตะ Number/parseFloat
    safety_stock: fieldText(plant.safety_stock),
    unit_cost: fieldText(plant.unit_cost),
    valuation_class: fieldText(plant.valuation_class),
  };
}

/** ฟอร์มเริ่มต้นคือค่าที่เซิร์ฟเวอร์ยืนยันล่าสุด ไม่ใช่ค่าว่างหรือค่าที่ค้างไว้ */
function formFromProduct(product: Product): EditFormState {
  return {
    name: product.name,
    model: fieldText(product.model),
    product_type: product.product_type,
    base_uom: product.base_uom,
    steel: steelTextsFromProduct(product),
    plants: product.plant_data.map(plantForm),
  };
}

/**
 * ตัดสินว่า `model` ที่จะส่งใน body คืออะไร — สามทางที่ต่างกันจริง (D-107 ข้อ 1)
 *
 * เทียบค่าที่กำลังพิมพ์ (ตัด whitespace แล้ว) กับค่าที่โหลดมาตอนแรก (ก็ตัดผ่าน
 * `fieldText()` มาแล้วเหมือนกัน — `null` กลายเป็น `''`):
 * - **เท่ากัน** → `undefined` ให้ `buildBody()` ตัดคีย์ทิ้งทั้งคีย์ = "ไม่แตะ"
 *   ครอบคลุมทั้งกรณีไม่ได้แก้อะไรเลย และกรณีพิมพ์แล้วลบกลับมาเป็นค่าเดิมเป๊ะ
 * - **ไม่เท่ากันและว่าง** → เดิมมีค่าแล้วผู้ใช้ลบออกจนหมด = ตั้งใจล้างค่า → `null`
 * - **ไม่เท่ากันและไม่ว่าง** → ค่าใหม่ → ส่ง string ตามที่พิมพ์ (ตัด whitespace)
 *
 * ผลคือ "ไม่แตะ" กับ "ล้างค่า" แยกออกจากกันด้วยการเทียบค่า ไม่ใช่ด้วย flag ที่ต้อง
 * จำสถานะเพิ่ม และช่องว่างที่ไม่เคยมีอะไรอยู่แต่แรกจะไม่ถูกส่งเป็น `null` โดยไม่จำเป็น
 */
function modelPatchValue(current: string, original: string | null): string | null | undefined {
  const trimmed = current.trim();
  const originalText = original ?? '';
  if (trimmed === originalText) {
    return undefined;
  }
  return trimmed === '' ? null : trimmed;
}

type BuildResult =
  | { readonly ok: true; readonly body: UpdateProductBody }
  | { readonly ok: false; readonly problems: readonly string[] };

/**
 * ประกอบ body จากฟอร์ม — คีย์ที่ออกไปถูกเขียนไว้ตรงนี้ทีละตัว
 *
 * ไม่ spread state ของฟอร์มทั้งก้อนด้วยเหตุผลเดียวกับหน้าจอสร้าง: การ spread คือ
 * วิธีที่ฟิลด์ซึ่งเซิร์ฟเวอร์เป็นเจ้าของจะหลุดออกไปในวันที่มีคนเพิ่มช่องใหม่
 *
 * ช่องว่างของฟิลด์ที่ nullable ได้ถูกส่งเป็น `null` (= ล้างค่า) ไม่ใช่ตัดคีย์ทิ้ง
 * (= ไม่แตะ) เพราะผู้ใช้ที่ลบค่าในช่องแล้วกดบันทึก ตั้งใจจะล้างค่านั้นจริง
 */
/**
 * ส่งเฉพาะช่องเหล็กที่เปลี่ยนจากค่าที่โหลดมา: เท่ากัน = ไม่แตะ, ว่างแต่เดิมมีค่า = `null` (ล้าง),
 * อย่างอื่น = ค่าที่พิมพ์ (D-165) — ชุดเหล็กตรวจกับค่าทั้งชุดหลังแก้ ไม่ใช่เฉพาะช่องที่ส่ง
 */
function steelPatch(form: SteelTexts, original: SteelTexts): Partial<Record<ProductSteelField, string | null>> {
  const patch: Partial<Record<ProductSteelField, string | null>> = {};
  for (const field of PRODUCT_STEEL_FIELDS) {
    const typed = form[field].trim();
    if (typed !== original[field]) {
      patch[field] = typed === '' ? null : typed;
    }
  }
  return patch;
}

function buildBody(
  form: EditFormState,
  originalModel: string | null,
  originalSteel: SteelTexts,
): BuildResult {
  const problems: string[] = [];
  const required: ReadonlyArray<readonly [GeneralField, string]> = [
    ['name', 'ชื่อสินค้า'],
    ['product_type', 'ประเภทสินค้า'],
    ['base_uom', 'หน่วยนับหลัก'],
  ];
  for (const [field, label] of required) {
    if (form[field].trim() === '') {
      problems.push(`ต้องกรอก${label}`);
    }
  }

  problems.push(...steelProblems(form.steel));

  const plants: UpdateProductPlantInput[] = [];
  for (const plant of form.plants) {
    const leadTime = plant.planned_delivery_time.trim();
    if (leadTime !== '' && !INTEGER_PATTERN.test(leadTime)) {
      problems.push(`Lead time ของโรงงาน ${plant.plant_code} ต้องเป็นจำนวนเต็มไม่ติดลบ (หน่วยเป็นวัน)`);
    }
    // คอลัมน์นี้เป็น NOT NULL ฝั่งฐานข้อมูล การส่งค่าว่างจึงถูกปฏิเสธแน่นอน —
    // บอกตั้งแต่ตรงนี้ดีกว่าปล่อยให้ไปตายที่เซิร์ฟเวอร์แล้วอ่านไม่ออกว่าช่องไหน
    if (plant.valuation_class.trim() === '') {
      problems.push(`ต้องกรอก Valuation class ของโรงงาน ${plant.plant_code}`);
    }

    plants.push({
      plant_code: plant.plant_code,
      valuation_class: plant.valuation_class.trim(),
      lot_size_procedure:
        plant.lot_size_procedure.trim() === '' ? null : plant.lot_size_procedure.trim(),
      planned_delivery_time: leadTime === '' ? null : Number(leadTime),
      // ⚠️ ไม่ trim และไม่แปลงชนิด: ค่าที่ผู้ใช้พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
      safety_stock: plant.safety_stock === '' ? null : plant.safety_stock,
      unit_cost: plant.unit_cost === '' ? null : plant.unit_cost,
    });
  }

  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const modelValue = modelPatchValue(form.model, originalModel);

  return {
    ok: true,
    body: {
      name: form.name.trim(),
      // ไม่แตะ (undefined) / ล้างค่า (null) / ตั้งค่าใหม่ (string) — ดู `modelPatchValue()`
      ...(modelValue === undefined ? {} : { model: modelValue }),
      product_type: form.product_type.trim(),
      base_uom: form.base_uom.trim(),
      ...(steelPatch(form.steel, originalSteel) as UpdateProductBody),
      // รายการว่างไม่ใช่ "ลบโรงงานทั้งหมด" แต่คือ "ไม่มีโรงงานที่คุณเห็น" — ไม่ส่งคีย์ไปเลย
      ...(plants.length === 0 ? {} : { plant_data: plants }),
    },
  };
}

/** ค่าที่เซิร์ฟเวอร์ยืนยันล่าสุด แสดงคู่กับฟอร์มเพื่อให้เทียบได้ว่าอะไรยังไม่ถูกบันทึก */
function SavedRecordPanel({ product }: { readonly product: Product }) {
  return (
    <section className="cap-card cap-card--table" aria-label="ค่าที่บันทึกไว้จริงในระบบ">
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          ค่าที่บันทึกไว้จริงในระบบตอนนี้
          <InfoHint
            label="เกี่ยวกับก้อนค่าที่บันทึกไว้จริง"
            text={
              <span data-testid="product-edit-saved-note">
                ก้อนนี้เปลี่ยนเมื่อเซิร์ฟเวอร์ตอบรับการบันทึกเท่านั้น — ถ้าบันทึกไม่สำเร็จ
                ค่าที่เห็นตรงนี้คือค่าที่ยังใช้งานอยู่จริง ไม่ใช่ค่าที่กำลังพิมพ์ในฟอร์ม
              </span>
            }
          />
        </h2>
      </div>
      <div className="cap-meta cap-meta--flush">
        <div className="cap-meta__item">
          <div className="cap-meta__label">ชื่อสินค้า</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-name">
            {product.name}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">รุ่น/โมเดล</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-model">
            {orDash(product.model)}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">ประเภทสินค้า</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-product_type">
            {product.product_type}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">หน่วยนับหลัก</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-base_uom">
            {product.base_uom}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">น้ำหนักต่อชิ้น (กก.)</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-kg_per_piece">
            {orDash(product.kg_per_piece)}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">เหล็ก</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-steel">
            {steelSummary(product)}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">สถานะ (D-011)</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-status">
            {productStatusLabel(product.status)}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">แก้ไขล่าสุด</div>
          <div className="cap-meta__value" data-testid="product-edit-saved-updated_at">
            {product.updated_at}
          </div>
        </div>
      </div>
      {product.plant_data.length === 0 ? null : (
        <div className="cap-table-wrap">
          <table className="cap-table">
            <caption className="cap-visually-hidden">
              ค่าระดับโรงงานที่บันทึกไว้จริง เฉพาะโรงงานที่ผู้ใช้ปัจจุบันมีสิทธิ์เห็น
            </caption>
            <thead>
              <tr>
                <th scope="col">โรงงาน</th>
                <th scope="col">โหมดเติมเต็ม</th>
                <th scope="col">วิธีกำหนดขนาดล็อต</th>
                <th scope="col">Lead time (วัน)</th>
                <th scope="col">สต๊อกปลอดภัย</th>
                <th scope="col">ต้นทุนต่อหน่วย</th>
                <th scope="col">Valuation class</th>
              </tr>
            </thead>
            <tbody>
              {product.plant_data.map((plant) => (
                <tr key={plant.plant_id} data-testid={`product-edit-saved-plant-${plant.plant_code}`}>
                  <th scope="row" className="cap-table__row-head">
                    {plant.plant_code}
                  </th>
                  <td className="cap-table__code">{plant.default_replenishment_mode}</td>
                  <td className="cap-table__muted">{orDash(plant.lot_size_procedure)}</td>
                  <td className="cap-table__code">{orDash(plant.planned_delivery_time)}</td>
                  <td className="cap-table__code">{orDash(plant.safety_stock)}</td>
                  <td
                    className="cap-table__code"
                    data-testid={`product-edit-saved-unit-cost-${plant.plant_code}`}
                  >
                    {orDash(plant.unit_cost)}
                  </td>
                  <td className="cap-table__muted">{orDash(plant.valuation_class)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ProductEditView({ productId, apiBaseUrl, fetchImpl }: ProductEditViewProps) {
  const { pathname } = useLocation();
  const [reloadToken, setReloadToken] = useState(0);
  const [record, setRecord] = useState<RecordState>({ status: 'loading' });
  const [form, setForm] = useState<EditFormState | null>(null);
  const [save, setSave] = useState<SaveState>({ status: 'idle' });

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setRecord({ status: 'loading' });
    setForm(null);
    setSave({ status: 'idle' });

    void fetchProduct(productId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setRecord(result);
      if (result.status === 'ready') {
        setForm(formFromProduct(result.product));
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [productId, apiBaseUrl, fetchImpl, reloadToken]);

  const product = record.status === 'ready' ? record.product : null;
  const editable = product !== null && product.status === PRODUCT_EDITABLE_STATUS;

  const updateField = (field: GeneralField, value: string) => {
    setForm((current) => (current === null ? current : { ...current, [field]: value }));
  };

  const updateSteel = (field: ProductSteelField, value: string) => {
    setForm((current) =>
      current === null ? current : { ...current, steel: { ...current.steel, [field]: value } },
    );
  };

  const updatePlant = (
    plantCode: string,
    field: Exclude<keyof PlantFormState, 'plant_code'>,
    value: string,
  ) => {
    setForm((current) =>
      current === null
        ? current
        : {
            ...current,
            plants: current.plants.map((plant) =>
              plant.plant_code === plantCode ? { ...plant, [field]: value } : plant,
            ),
          },
    );
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (form === null || save.status === 'saving' || product === null) {
      return;
    }

    const built = buildBody(form, product.model, steelTextsFromProduct(product));
    if (!built.ok) {
      setSave({ status: 'invalid-form', problems: built.problems });
      return;
    }

    setSave({ status: 'saving' });
    const result = await updateProduct(productId, built.body, { apiBaseUrl, fetchImpl });
    if (!mounted.current) {
      return;
    }

    if (result.status === 'ready') {
      // สิ่งที่แสดงต่อจากนี้คือ record ที่เซิร์ฟเวอร์ตอบกลับมา ไม่ใช่ค่าที่พิมพ์ไว้ —
      // ค่าที่เซิร์ฟเวอร์ normalize (เช่น "42.5" → "42.5000") จึงปรากฏบนจอตามจริง
      setRecord({ status: 'ready', product: result.product });
      setForm(formFromProduct(result.product));
      setSave({ status: 'saved' });
      return;
    }

    // ⚠️ ไม่แตะ `record` และไม่แตะ `form`: ความล้มเหลวไม่ใช่การบันทึก ค่าที่เซิร์ฟเวอร์
    // ยืนยันจึงยังเป็นค่าเดิม ส่วนสิ่งที่ผู้ใช้พิมพ์ไว้ยังอยู่ให้แก้ต่อได้ โดยมีคำเตือน
    // กำกับว่ายังไม่ถูกบันทึก — ไม่ใช่ปล่อยให้อ่านเหมือนบันทึกไปแล้ว
    setSave(result);
  };

  /**
   * แจกแจงสถานะที่ "ไม่ใช่ความล้มเหลวจากเซิร์ฟเวอร์" ออกไป แล้วให้ TypeScript
   * เป็นผู้ยืนยันว่าที่เหลือคือ `ProductFailure` จริง — แนวเดียวกับหน้าจอสร้าง ไม่ใช้ cast
   */
  const saveFailure: ProductFailure | null =
    save.status === 'idle' ||
    save.status === 'saving' ||
    save.status === 'saved' ||
    save.status === 'invalid-form'
      ? null
      : save;

  return (
    <div className="cap-page" data-testid="product-edit">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="product-edit-code">
            {product === null ? 'แก้ไขข้อมูลสินค้า' : product.product_code}
            <InfoHint
              label="เกี่ยวกับการแก้ไขข้อมูลสินค้า"
              text="แก้ไขได้เฉพาะตอนที่รายการยังเป็นร่าง (DRAFT) เท่านั้น"
            />
          </h1>
          {product === null ? <p className="cap-page__sub">{`รหัสภายใน ${productId}`}</p> : null}
        </div>
        <div className="cap-page__actions">
          {product === null ? null : (
            <StatusBadge tone={productStatusTone(product.status)}>
              {productStatusLabel(product.status)}
            </StatusBadge>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="product-edit-back"
            to={{ pathname, search: detailSearch(productId) }}
          >
            กลับไปหน้ารายละเอียด
          </Link>
        </div>
      </div>

      {record.status === 'loading' ? (
        <div className="cap-notice" data-testid="product-edit-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดข้อมูลสินค้าเพื่อแก้ไข…</p>
        </div>
      ) : null}

      {record.status !== 'loading' && record.status !== 'ready' ? (
        <ProductFailureNotice
          failure={record}
          context="detail"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {product !== null && !editable ? (
        <div className="cap-notice" data-testid="product-edit-not-draft" role="status">
          <StatusBadge tone="warning">แก้ไขจากหน้าจอนี้ไม่ได้</StatusBadge>
          <h2 className="cap-notice__title">
            รายการนี้อยู่ในสถานะ “{productStatusLabel(product.status)}” จึงไม่เปิดฟอร์มให้แก้
          </h2>
          <p className="cap-notice__text">
            <code className="cap-table__code">PATCH /v1/products/{'{product_id}'}</code>{' '}
            แก้ได้เฉพาะรายการที่ยังเป็น “ร่าง (DRAFT)” ตามตาราง endpoint ใน contract —
            ถ้ากดบันทึกจากหน้านี้ เซิร์ฟเวอร์จะตอบ 422 กลับมาเสมอ
          </p>
          <ProductChangeRequestNote productId={productId} />
        </div>
      ) : null}

      {save.status === 'saved' ? (
        <div className="cap-banner" role="status" data-testid="product-edit-saved">
          <strong>บันทึกการแก้ไขแล้ว</strong>
          <span>
            ค่าที่แสดงด้านล่างคือค่าที่เซิร์ฟเวอร์ตอบกลับมาหลังบันทึก ไม่ใช่ค่าที่พิมพ์ไว้ —
            สถานะยังเป็น “ร่าง (DRAFT)” และยังต้องส่งอนุมัติจากหน้ารายละเอียด
          </span>
        </div>
      ) : null}

      {save.status === 'invalid-form' ? (
        <div
          className="cap-banner cap-banner--warning"
          role="status"
          data-testid="product-edit-invalid"
        >
          <strong>ยังบันทึกไม่ได้ — ข้อมูลไม่ครบ</strong>
          <span>{save.problems.join(' · ')}</span>
        </div>
      ) : null}

      {saveFailure === null ? null : (
        <>
          <ProductFailureNotice failure={saveFailure} context="edit" productId={productId} />
          <p className="cap-banner__note" data-testid="product-edit-unsaved">
            ค่าที่กรอกค้างไว้ในฟอร์มยังไม่ถูกบันทึก และหน้าจอจะไม่ส่งซ้ำให้เอง —
            แก้แล้วกดบันทึกอีกครั้ง หรือกด “คืนค่าจากเซิร์ฟเวอร์” เพื่อทิ้งสิ่งที่พิมพ์ไว้
          </p>
        </>
      )}

      {product === null ? null : <SavedRecordPanel product={product} />}

      {product === null || form === null || !editable ? null : (
        <form
          className="cap-form"
          data-testid="product-edit-form"
          onSubmit={(event) => void onSubmit(event)}
          noValidate
        >
          <section className="cap-card cap-card--form" aria-label="ข้อมูลทั่วไป">
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                ข้อมูลทั่วไป
                <InfoHint label="เกี่ยวกับขอบเขตของข้อมูลทั่วไป" text="ใช้ร่วมกันทุกโรงงาน" />
              </h2>
            </div>
            <div className="cap-form__grid">
              <label className="cap-field">
                <span className="cap-field__label">รหัสสินค้า (product_code)</span>
                <input
                  className="cap-input"
                  name="product_code"
                  data-testid="product-edit-product-code"
                  value={product.product_code}
                  readOnly
                />
                <InfoHint
                  label="เกี่ยวกับการแก้รหัสสินค้า"
                  text={
                    <span data-testid="product-edit-code-note">
                      แก้ไม่ได้และไม่ถูกส่งไปกับคำขอ — รหัสธุรกิจของรายการที่สร้างแล้วเปลี่ยนไม่ได้
                      เพราะเป็นรหัสที่เอกสารและงานอื่นอ้างถึงไปแล้ว ถ้ารหัสผิดจริงต้องสร้างรายการใหม่
                    </span>
                  }
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">ชื่อสินค้า (name)</span>
                <input
                  className="cap-input"
                  name="name"
                  value={form.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  required
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">รุ่น/โมเดล (model)</span>
                <input
                  className="cap-input"
                  name="model"
                  data-testid="product-edit-model"
                  value={form.model}
                  onChange={(event) => updateField('model', event.target.value)}
                />
                <InfoHint
                  label="เกี่ยวกับรุ่น/โมเดล"
                  text={
                    <>
                      ไม่บังคับ — เว้นว่างเพื่อล้างรุ่น/โมเดลที่เคยบันทึกไว้ (ส่ง{' '}
                      <code className="cap-table__code">null</code>) ไม่แตะช่องนี้เพื่อคงค่าเดิมไว้
                    </>
                  }
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">ประเภทสินค้า (product_type)</span>
                <input
                  className="cap-input"
                  name="product_type"
                  value={form.product_type}
                  onChange={(event) => updateField('product_type', event.target.value)}
                  placeholder="RAW"
                  required
                />
              </label>
              <label className="cap-field">
                <span className="cap-field__label">หน่วยนับหลัก (base_uom)</span>
                <input
                  className="cap-input"
                  name="base_uom"
                  value={form.base_uom}
                  onChange={(event) => updateField('base_uom', event.target.value)}
                  placeholder="EA"
                  required
                />
                <InfoHint
                  label="เกี่ยวกับหน่วยนับหลัก"
                  text="เฟส 1 มีหน่วยนับหลักเดียวต่อสินค้า"
                />
              </label>
            </div>
            <ProductSteelFields idPrefix="product-edit" value={form.steel} onChange={updateSteel} />
          </section>

          {form.plants.length === 0 ? (
            <section className="cap-card cap-card--form" aria-label="ข้อมูลระดับโรงงาน">
              <div className="cap-card__head">
                <h2 className="cap-card__title">ข้อมูลระดับโรงงาน</h2>
              </div>
              <p className="cap-card__foot" data-testid="product-edit-plant-empty">
                ไม่มีโรงงานที่คุณมีสิทธิ์เห็นสำหรับสินค้านี้ (D-004) หน้าจอนี้จึงแก้ได้เฉพาะ
                ข้อมูลทั่วไป และจะไม่ส่ง <code className="cap-table__code">plant_data</code> ไปกับคำขอ
              </p>
            </section>
          ) : (
            form.plants.map((plant) => (
              <section
                className="cap-card cap-card--form"
                key={plant.plant_code}
                aria-label={`ข้อมูลระดับโรงงาน ${plant.plant_code}`}
                data-testid={`product-edit-plant-${plant.plant_code}`}
              >
                <div className="cap-card__head">
                  <h2 className="cap-card__title">
                    โรงงาน {plant.plant_code}
                    <InfoHint
                      label={`เกี่ยวกับขอบเขตของโรงงาน ${plant.plant_code}`}
                      text="แก้เฉพาะโรงงานที่คุณมีสิทธิ์เห็น (D-004) — โรงงานอื่นที่มีสินค้านี้อยู่ ไม่ถูกแตะจากการบันทึกครั้งนี้"
                    />
                  </h2>
                </div>
                <div className="cap-form__grid">
                  <label className="cap-field">
                    <span className="cap-field__label">รหัสโรงงาน (plant_code)</span>
                    <input
                      className="cap-input"
                      name={`plant_code_${plant.plant_code}`}
                      value={plant.plant_code}
                      readOnly
                    />
                    <InfoHint
                      label={`เกี่ยวกับรหัสโรงงาน ${plant.plant_code}`}
                      text="เป็นคีย์ที่ใช้จับคู่กับข้อมูลเดิม ไม่ใช่ค่าที่แก้ได้จากหน้านี้"
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">
                      โหมดเติมเต็ม (default_replenishment_mode)
                    </span>
                    <input
                      className="cap-input"
                      name={`default_replenishment_mode_${plant.plant_code}`}
                      value={
                        product.plant_data.find((row) => row.plant_code === plant.plant_code)
                          ?.default_replenishment_mode ?? ''
                      }
                      readOnly
                    />
                    <InfoHint
                      label={`เกี่ยวกับโหมดเติมเต็มของโรงงาน ${plant.plant_code}`}
                      text={
                        <span data-testid="product-edit-push-note">
                          เฟส 1 ผลิตแบบ PUSH เท่านั้น (D-034) หน้าจอนี้จึงไม่ส่งค่านี้ไปกับคำขอ
                        </span>
                      }
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">Valuation class</span>
                    <input
                      className="cap-input"
                      name={`valuation_class_${plant.plant_code}`}
                      data-testid={`product-edit-valuation-class-${plant.plant_code}`}
                      value={plant.valuation_class}
                      onChange={(event) =>
                        updatePlant(plant.plant_code, 'valuation_class', event.target.value)
                      }
                      required
                    />
                    <InfoHint
                      label={`เกี่ยวกับ Valuation class ของโรงงาน ${plant.plant_code}`}
                      text="ต้องมีค่าเสมอ — เว้นว่างไม่ได้"
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">Lead time (วัน)</span>
                    <input
                      className="cap-input"
                      name={`planned_delivery_time_${plant.plant_code}`}
                      data-testid={`product-edit-lead-time-${plant.plant_code}`}
                      inputMode="numeric"
                      value={plant.planned_delivery_time}
                      onChange={(event) =>
                        updatePlant(plant.plant_code, 'planned_delivery_time', event.target.value)
                      }
                      placeholder="7"
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">วิธีกำหนดขนาดล็อต</span>
                    <input
                      className="cap-input"
                      name={`lot_size_procedure_${plant.plant_code}`}
                      data-testid={`product-edit-lot-size-${plant.plant_code}`}
                      value={plant.lot_size_procedure}
                      onChange={(event) =>
                        updatePlant(plant.plant_code, 'lot_size_procedure', event.target.value)
                      }
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">สต๊อกปลอดภัย (safety_stock)</span>
                    <input
                      className="cap-input"
                      name={`safety_stock_${plant.plant_code}`}
                      data-testid={`product-edit-safety-stock-${plant.plant_code}`}
                      inputMode="decimal"
                      value={plant.safety_stock}
                      onChange={(event) =>
                        updatePlant(plant.plant_code, 'safety_stock', event.target.value)
                      }
                      placeholder="100.0000"
                    />
                  </label>
                  <label className="cap-field">
                    <span className="cap-field__label">ต้นทุนต่อหน่วย (unit_cost)</span>
                    <input
                      className="cap-input"
                      name={`unit_cost_${plant.plant_code}`}
                      data-testid={`product-edit-unit-cost-${plant.plant_code}`}
                      inputMode="decimal"
                      value={plant.unit_cost}
                      onChange={(event) =>
                        updatePlant(plant.plant_code, 'unit_cost', event.target.value)
                      }
                      placeholder="42.5000"
                    />
                    <InfoHint
                      label={`เกี่ยวกับการส่งค่าต้นทุนต่อหน่วยของโรงงาน ${plant.plant_code}`}
                      text="ส่งเป็นข้อความตามที่พิมพ์ทุกตัวอักษร เพื่อไม่ให้ทศนิยมของ NUMERIC(18,4) หายไป"
                    />
                  </label>
                </div>
              </section>
            ))
          )}

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="product-edit-submit"
              disabled={save.status === 'saving'}
            >
              {save.status === 'saving' ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="product-edit-revert"
              disabled={save.status === 'saving'}
              onClick={() => {
                setForm(formFromProduct(product));
                setSave({ status: 'idle' });
              }}
            >
              คืนค่าจากเซิร์ฟเวอร์
            </button>
            <InfoHint
              label="เกี่ยวกับฟิลด์ที่คำขอแก้ไขนี้ส่งไป"
              text={
                <>
                  คำขอนี้ส่งเฉพาะ <code className="cap-table__code">name</code>,{' '}
                  <code className="cap-table__code">model</code> (เมื่อแก้หรือล้างค่า),{' '}
                  <code className="cap-table__code">product_type</code>,{' '}
                  <code className="cap-table__code">base_uom</code> และ{' '}
                  <code className="cap-table__code">plant_data</code> — รหัสสินค้า สถานะ
                  และผู้ดำเนินการ ไม่ถูกส่งไปเลย
                </>
              }
            />
          </div>
        </form>
      )}
    </div>
  );
}
